import { useState, useEffect, useRef, createContext, useContext } from "react";
import { supabase } from "@/integrations/supabase/client";

// Global counter per page load — only 1 auto-popup per visit.
// Reviewers said the constant Discord/onboarding nags feel like too much; the
// blue-glow icon stays so users can still tap-to-discover later.
let autoPopupCount = 0;
const MAX_AUTO_POPUPS = 1;

// ── Single-popup lock ──────────────────────────────────────────────
// Only one auto-popup can be visible at a time so dismissing one
// doesn't accidentally close another that opened at the same instant.
let activePopupId: string | null = null;
const popupListeners = new Set<() => void>();
const notifyPopupChange = () => popupListeners.forEach((fn) => fn());

// Reset counter on route changes (called externally or on mount)
export const resetAutoPopupCounter = () => {
  autoPopupCount = 0;
  activePopupId = null;
  notifyPopupChange();
};

// ── DB-synced dismissal cache ─────────────────────────────────────
// When the user is signed in we sync hint dismissals to the DB so
// they carry across devices. We cache the full set in memory after
// the first load to avoid a query per <FirstTimeHint /> mount.
type CachedDismissals = Map<string, 1 | 2>;
let dbDismissalsCache: CachedDismissals | null = null;
let dbDismissalsLoading: Promise<CachedDismissals> | null = null;
let dbDismissalsUserId: string | null = null;
const dbCacheListeners = new Set<() => void>();
const notifyDbCache = () => dbCacheListeners.forEach((fn) => fn());

const loadDbDismissals = async (userId: string): Promise<CachedDismissals> => {
  if (dbDismissalsUserId === userId && dbDismissalsCache) return dbDismissalsCache;
  if (dbDismissalsLoading && dbDismissalsUserId === userId) return dbDismissalsLoading;
  dbDismissalsUserId = userId;
  dbDismissalsLoading = (async () => {
    const { data } = await supabase
      .from("user_hint_dismissals")
      .select("hint_id, stage")
      .eq("user_id", userId);
    const map: CachedDismissals = new Map();
    (data || []).forEach((r: any) => map.set(r.hint_id, r.stage as 1 | 2));
    dbDismissalsCache = map;
    notifyDbCache();
    return map;
  })();
  return dbDismissalsLoading;
};

const persistDismissal = async (hintId: string, stage: 1 | 2) => {
  // Always update local cache so other mounted hints react immediately
  if (dbDismissalsCache) dbDismissalsCache.set(hintId, stage);
  notifyDbCache();

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return; // anon → localStorage only (handled by caller)
  await supabase
    .from("user_hint_dismissals")
    .upsert(
      { user_id: user.id, hint_id: hintId, stage },
      { onConflict: "user_id,hint_id" }
    );
};

/** Context to force all FirstTimeHints to show as fresh (used in test/preview modes) */
const HintForceResetContext = createContext(false);
export const HintForceResetProvider = HintForceResetContext.Provider;

import { Info, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface FirstTimeHintProps {
  id: string;
  title: string;
  description: string;
  side?: "top" | "bottom" | "left" | "right";
  className?: string;
  iconSize?: "sm" | "md";
  /** When true, ignore localStorage and always start as "fresh" (for test/preview modes) */
  forceReset?: boolean;
}

export const STORAGE_PREFIX = "tgh_hint_seen_";

// Stage flow: "fresh" (popup auto-shows) → "blue" (dismissed popup, icon is blue) → "done" (icon is white/muted)
type HintStage = "fresh" | "blue" | "done";

const readLocalStage = (storageKey: string): HintStage => {
  try {
    const v = localStorage.getItem(storageKey);
    if (v === "2") return "done";
    if (v === "1") return "blue";
    return "fresh";
  } catch { return "fresh"; }
};

const FirstTimeHint = ({ id, title, description, side = "bottom", className, iconSize = "sm", forceReset: forceResetProp }: FirstTimeHintProps) => {
  const contextForceReset = useContext(HintForceResetContext);
  const forceReset = forceResetProp || contextForceReset;
  const storageKey = `${STORAGE_PREFIX}${id}`;
  const [stage, setStage] = useState<HintStage>(() => forceReset ? "fresh" : readLocalStage(storageKey));
  const [open, setOpen] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const [flipAlign, setFlipAlign] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // ── Sync from DB on mount (signed-in users) ──
  useEffect(() => {
    if (forceReset) return;
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) return;
      const map = await loadDbDismissals(user.id);
      if (cancelled) return;
      const dbStage = map.get(id);
      if (dbStage === 2) setStage("done");
      else if (dbStage === 1) setStage((s) => s === "fresh" ? "blue" : s);
    })();
    // React to other hints updating the cache
    const listener = () => {
      if (!dbDismissalsCache) return;
      const dbStage = dbDismissalsCache.get(id);
      if (dbStage === 2) setStage("done");
      else if (dbStage === 1) setStage((s) => s === "fresh" ? "blue" : s);
    };
    dbCacheListeners.add(listener);
    return () => { cancelled = true; dbCacheListeners.delete(listener); };
  }, [id, forceReset]);

  // Reset to fresh when forceReset becomes true (e.g. entering test mode or switching member)
  useEffect(() => {
    if (forceReset) {
      setStage("fresh");
      setShowPopover(false);
      setOpen(false);
    }
  }, [forceReset]);

  // Check if popup would overflow left edge and flip to left-aligned if needed
  useEffect(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    if (rect.left < 300) {
      setFlipAlign(true);
    }
  }, [open, showPopover]);

  // On first mount, if fresh, try to claim the single-popup slot.
  // If another hint is already showing, wait for it to close before trying again.
  useEffect(() => {
    if (stage !== "fresh") return;
    if (autoPopupCount >= MAX_AUTO_POPUPS) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const tryClaim = () => {
      if (cancelled || stage !== "fresh") return;
      if (activePopupId !== null) return; // wait for current popup to close
      if (autoPopupCount >= MAX_AUTO_POPUPS) return;
      activePopupId = id;
      autoPopupCount++;
      notifyPopupChange();
      setShowPopover(true);
    };

    // Initial delay so the page settles
    timer = setTimeout(tryClaim, 600);

    // Also retry whenever another popup releases the lock
    const listener = () => tryClaim();
    popupListeners.add(listener);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      popupListeners.delete(listener);
      // Release the lock if this popup is the active one and is unmounting
      if (activePopupId === id) {
        activePopupId = null;
        notifyPopupChange();
      }
    };
  }, [stage, id]);

  // Dismiss the auto-popup → move to "blue" stage
  const dismissPopover = () => {
    setShowPopover(false);
    setStage("blue");
    if (activePopupId === id) {
      activePopupId = null;
      notifyPopupChange();
    }
    if (!forceReset) {
      try { localStorage.setItem(storageKey, "1"); } catch {}
      persistDismissal(id, 1);
    }
  };

  const toggleInfo = () => {
    if (showPopover) {
      dismissPopover();
    } else if (stage === "blue" && !open) {
      setOpen(true);
      setStage("done");
      if (!forceReset) {
        try { localStorage.setItem(storageKey, "2"); } catch {}
        persistDismissal(id, 2);
      }
    } else {
      setOpen((o) => !o);
    }
  };

  const isVisible = showPopover || open;
  const isBlue = stage === "fresh" || stage === "blue";

  const align = flipAlign ? "left-0" : "right-0";
  const positionClasses: Record<string, string> = {
    top: `bottom-full ${align} mb-2`,
    bottom: `top-full ${align} mt-2`,
    left: "right-full top-1/2 -translate-y-1/2 mr-2",
    right: "left-full top-1/2 -translate-y-1/2 ml-2",
  };

  const arrowAlign = flipAlign ? "left-3" : "right-3";

  const arrowClasses: Record<string, string> = {
    top: `top-full ${arrowAlign} border-l-transparent border-r-transparent border-b-transparent border-t-border`,
    bottom: `bottom-full ${arrowAlign} border-l-transparent border-r-transparent border-t-transparent border-b-border`,
    left: "left-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-border",
    right: "right-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-border",
  };

  const iconPx = iconSize === "sm" ? "w-3.5 h-3.5" : "w-4 h-4";

  return (
    <div ref={ref} className={cn("relative inline-flex items-center overflow-visible", className)}>
      <button
        onClick={toggleInfo}
        className={cn(
          "relative inline-flex items-center justify-center rounded-full p-0.5 transition-all duration-200 focus:outline-none hover:scale-105",
          isBlue ? "text-primary" : "text-muted-foreground",
          showPopover && "animate-pulse"
        )}
        aria-label={`Info: ${title}`}
        type="button"
      >
        {isBlue && (
          <>
            <motion.span
              aria-hidden="true"
              className="pointer-events-none absolute inset-[-4px] rounded-full bg-primary/10 blur-sm"
              animate={{
                scale: [1, 1.08, 1],
                opacity: [0.5, 0.7, 0.5],
              }}
              transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
              style={{
                boxShadow: "0 0 10px hsl(var(--primary) / 0.35)",
              }}
            />
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-[-1px] rounded-full border border-primary/25 bg-primary/5"
            />
          </>
        )}
        <Info
          className={cn(iconPx, "relative z-10", isBlue && "drop-shadow-[0_0_4px_hsl(var(--primary)/0.6)]")}
          strokeWidth={2.5}
        />
      </button>

      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{ duration: 0.15 }}
            onMouseDown={(e) => e.stopPropagation()}
            className={cn(
              "absolute z-50 w-64 rounded-lg border bg-popover/95 backdrop-blur-sm shadow-lg p-3",
              positionClasses[side],
              (showPopover || stage === "blue") && "ring-1 ring-primary/30"
            )}
          >
            {/* Arrow */}
            <span className={cn("absolute w-0 h-0 border-[6px]", arrowClasses[side])} />

            <div className="flex items-start justify-between gap-2 mb-1">
              <h4 className="font-heading text-xs font-bold text-foreground leading-tight">{title}</h4>
              <button
                onClick={() => showPopover ? dismissPopover() : setOpen(false)}
                className="text-muted-foreground hover:text-foreground shrink-0 -mt-0.5"
                type="button"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
            <p className="font-body text-[11px] text-muted-foreground leading-relaxed">{description}</p>

            {showPopover && (
              <button
                onClick={dismissPopover}
                className="mt-2 w-full text-[10px] font-heading font-bold text-primary hover:text-primary/80 transition-colors text-center py-1 rounded border border-primary/20 hover:bg-primary/5"
                type="button"
              >
                Got it
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FirstTimeHint;

/** Utility to reset all hints (for testing) — clears local + DB if signed in */
export const resetAllHints = async () => {
  Object.keys(localStorage).forEach((k) => {
    if (k.startsWith(STORAGE_PREFIX)) localStorage.removeItem(k);
  });
  // Clear in-memory cache
  if (dbDismissalsCache) dbDismissalsCache.clear();
  notifyDbCache();
  // Clear server-side dismissals if signed in
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase.from("user_hint_dismissals").delete().eq("user_id", user.id);
    }
  } catch {}
};
